import { Eyebrow, Reveal, SectionTitle } from "@/components/site/Primitives";
import { DiscoveryBand } from "@/components/site/Blocks";
import { FounderPhoto } from "@/components/site/FounderPhoto";
import { useT } from "@/lib/i18n";

export default function About() {
  const t = useT();
  const a = t.about;
  return (
    <div data-testid="about-page">
      <section data-testid="page-hero" className="relative overflow-hidden pb-16 pt-32 sm:pt-40 lg:pb-24">
        <div className="container-x grid items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal><Eyebrow>{a.eyebrow}</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h1 data-testid="about-name" className="mt-6 font-display text-[2.7rem] leading-[1] text-forest sm:text-6xl lg:text-[4.6rem]">{a.name}</h1>
              <p data-testid="about-subtitle" className="mt-4 font-display text-2xl italic leading-snug text-gold-text sm:text-3xl">{a.subtitle}</p>
            </Reveal>
            <Reveal delay={0.16}>
              <div data-testid="about-intro" className="mt-7 max-w-2xl space-y-5 text-base leading-relaxed text-forest/80 sm:text-lg">
                {a.intro.map((p) => <p key={p}>{p}</p>)}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <FounderPhoto className="mx-auto aspect-[4/5] w-full max-w-[420px]" testid="about-founder-photo" />
            </Reveal>
          </div>
        </div>
        <div className="container-x mt-14">
          <img src="/img/piran-church.jpg" alt="Piran, Slovenija" loading="lazy" data-testid="about-piran-photo" className="h-56 w-full rounded-[28px] object-cover sm:h-72 lg:h-80" />
        </div>
      </section>
      <section data-testid="about-education" className="bg-ivory-card py-20 sm:py-28">
        <div className="container-x grid gap-14 lg:grid-cols-2 lg:gap-16">
          <div>
            <Reveal><SectionTitle title={a.eduTitle} /></Reveal>
            <LabelList items={a.edu} testid="about-edu-item" />
          </div>
          <div data-testid="about-skills">
            <Reveal><SectionTitle title={a.skillsTitle} /></Reveal>
            <LabelList items={a.skills} testid="about-skill-item" />
          </div>
        </div>
      </section>
      <section className="container-x pb-4 pt-4 sm:pt-8">
        <Approach w={a.approach} />
      </section>
      <section data-testid="about-closing" className="container-x py-20 sm:py-28">
        <Reveal><SectionTitle title={a.closingTitle} /></Reveal>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-forest/80 sm:text-lg">{a.closing}</p>
        <p data-testid="about-closing-invite" className="mt-8 max-w-3xl border-l-2 border-gold-text/60 pl-6 font-display text-2xl leading-snug text-forest sm:text-[1.7rem]">{a.closingInvite}</p>
      </section>
      <DiscoveryBand />
    </div>
  );
}

function LabelList({ items, testid }) {
  return (
    <ul className="mt-10 space-y-7">
      {items.map((s, i) => {
        const cut = s.indexOf(": ");
        return (
          <li key={s} data-testid={`${testid}-${i}`} className="border-t border-forest/15 pt-5 text-[15px] leading-relaxed text-forest/80 sm:text-base">
            <span className="font-semibold text-forest">{s.slice(0, cut + 1)}</span>{s.slice(cut + 1)}
          </li>
        );
      })}
    </ul>
  );
}

function Approach({ w }) {
  return (
    <div data-testid="about-approach" className="mt-16 rounded-[32px] bg-ivory-2 p-8 sm:p-12">
      <Eyebrow>{w.eyebrow}</Eyebrow>
      <h2 className="mt-4 font-display text-4xl text-forest">{w.title}</h2>
      <p data-testid="about-approach-intro" className="mt-6 max-w-3xl text-base leading-relaxed text-forest/80 sm:text-lg">{w.intro}</p>
      <ol className="mt-10 grid gap-x-12 gap-y-10 lg:grid-cols-2">
        {w.steps.map((s, i) => (
          <li key={s.title} data-testid={`about-approach-step-${i}`} className="flex flex-col gap-3 sm:flex-row sm:gap-5">
            <span className="w-12 shrink-0 font-display text-4xl italic leading-none text-gold-text">0{i + 1}</span>
            <div>
              <h3 className="font-display text-2xl leading-tight text-forest">{s.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-forest/75">{s.text}</p>
            </div>
          </li>
        ))}
      </ol>
      <div data-testid="about-approach-closing" className="mt-12 border-l-2 border-gold-text/60 bg-ivory-card/70 py-6 pl-6 pr-6 sm:pl-8">
        <h3 className="font-display text-2xl leading-tight text-forest">{w.closing.title}</h3>
        <p className="mt-3 text-[15px] leading-relaxed text-forest/80">{w.closing.text}</p>
      </div>
    </div>
  );
}
